
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useTeamInvitation = (setEmail: (email: string) => void, setIsLogin: (isLogin: boolean) => void) => {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const teamMemberId = searchParams.get("team_member_id");

  useEffect(() => {
    const fetchTeamMemberEmail = async () => {
      if (teamMemberId) {
        try {
          setLoading(true);
          const { data: teamMember, error } = await supabase
            .from("team_members")
            .select("email")
            .eq("id", teamMemberId)
            .maybeSingle();

          if (error) {
            console.error("Error fetching team member:", error);
            toast.error("Error fetching invitation details");
            return;
          }

          if (!teamMember) {
            toast.error("Invalid invitation link");
            return;
          }

          setEmail(teamMember.email);
          setIsLogin(false);
        } catch (error) {
          console.error("Error in fetchTeamMemberEmail:", error);
          toast.error("Error processing invitation");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchTeamMemberEmail();
  }, [teamMemberId, setEmail, setIsLogin]);

  const acceptTeamInvitation = async (userId: string) => {
    if (!teamMemberId) return false;

    try {
      setLoading(true);
      
      // Get the team member details including email and status
      const { data: teamMember, error: teamMemberError } = await supabase
        .from("team_members")
        .select("company_id, email, status, user_id")
        .eq("id", teamMemberId)
        .single();

      if (teamMemberError) throw new Error("Invalid or expired invitation");
      
      // Check if invitation is already accepted
      if (teamMember.status === 'active' && teamMember.user_id) {
        if (teamMember.user_id === userId) {
          toast.info("You've already accepted this invitation");
          return true;
        } else {
          throw new Error("This invitation has already been accepted by another user");
        }
      }
      
      // Get the current user's email to verify it matches the invitation
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      if (user?.email?.toLowerCase() !== teamMember.email.toLowerCase()) {
        throw new Error("Please sign in with the email address that received the invitation");
      }
      
      // Check if user already belongs to a company
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("id", userId)
        .maybeSingle();
        
      if (profileError) throw profileError;
      
      // Update the user's profile to set the new company_id
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ company_id: teamMember.company_id })
        .eq("id", userId);
        
      if (updateError) throw updateError;
      
      // Activate team membership
      const { error } = await supabase
        .from("team_members")
        .update({
          user_id: userId,
          status: "active",
          updated_at: new Date().toISOString()
        })
        .eq("id", teamMemberId);

      if (error) throw error;
      
      if (profile?.company_id) {
        toast.success("You've switched to the new team!");
      } else {
        toast.success("Successfully joined the team!");
      }
      
      return true;
    } catch (error: any) {
      toast.error("Failed to join team: " + error.message);
      return false;
    }
  };

  return {
    teamMemberId,
    loading,
    acceptTeamInvitation
  };
};
